<template>
  <div class="reserve-view">
    <!-- ページヘッダー -->
    <section class="page-hero">
      <div class="container">
        <h1 class="page-hero__title">Reserve</h1>
        <p class="page-hero__subtitle">ご予約・見学受付</p>
      </div>
    </section>

    <!-- 予約フォームセクション -->
    <base-section
      title="ご予約フォーム"
      subtitle="※事前に必ず見学をお願いします【平日10時から11時頃でお願いしています】"
    >
      <div class="reserve-form-wrapper">
        <form class="reserve-form" @submit.prevent>
          <div class="form-group">
            <label class="form-label">メールアドレス <span class="required">※必須</span></label>
            <div class="form-input-wrapper">
              <input 
                type="email"
                class="form-control" 
                v-model="email" 
                placeholder="例）info@kaon-r.jp"
                :class="{ 'has-error': errors.email }"
                @blur="checkCustomerStatus"
              >
              <div v-if="isCheckingCustomer" class="email-loading">
                <i class="fas fa-spinner fa-spin"></i>
                <span>顧客情報を確認中...</span>
              </div>
            </div>
            <p v-if="errors.email" class="error-message">{{ errors.email }}</p>
          </div>

          <div class="form-group">
            <label class="form-label">お名前 <span class="required">※必須</span></label>
            <input 
              class="form-control" 
              v-model="name" 
              placeholder="例）田中 太郎"
              :class="{ 'has-error': errors.name }"
            >
            <p v-if="errors.name" class="error-message">{{ errors.name }}</p>
          </div>

          <div class="form-group">
            <label class="form-label">フリガナ</label>
            <input 
              class="form-control" 
              v-model="kana" 
              placeholder="例）タナカ タロウ"
            >
          </div>

          <div class="form-group">
            <label class="form-label">電話番号 <span class="required">※必須</span></label>
            <input 
              type="tel"
              class="form-control" 
              v-model="tel" 
              placeholder="例）09012341234"
              :class="{ 'has-error': errors.tel }"
            >
            <p v-if="errors.tel" class="error-message">{{ errors.tel }}</p>
          </div>

          <div class="form-group">
            <label class="form-label">ご利用目的 <span class="required">※必須</span></label>
            <div class="reservation-type-options">
              <label 
                class="reservation-type-option"
                :class="{ 'disabled': customerStatus === 'returning' }"
              >
                <input 
                  type="radio" 
                  name="reservationType" 
                  value="見学"
                  v-model="reservationType"
                  :disabled="customerStatus === 'returning'"
                >
                <span>見学</span>
              </label>
              <label 
                class="reservation-type-option"
                :class="{ 'disabled': customerStatus === 'new' }"
              >
                <input 
                  type="radio" 
                  name="reservationType" 
                  value="レンタル"
                  v-model="reservationType"
                  :disabled="customerStatus === 'new'"
                >
                <span>レンタル</span>
              </label>
            </div>
            <p v-if="customerStatus === 'new'" class="info-message">
              <i class="fas fa-info-circle"></i>
              初めてのお客様は見学のみご予約いただけます
            </p>
            <p v-else-if="customerStatus === 'returning'" class="info-message">
              <i class="fas fa-check-circle"></i>
              リピーターのお客様はレンタルをご予約いただけます
            </p>
            <p v-if="errors.reservationType" class="error-message">{{ errors.reservationType }}</p>
          </div>

          <!-- 新しい予約カレンダーコンポーネント -->
          <div class="form-group">
            <label class="form-label">予約日時 <span class="required">※必須</span></label>
            <reserve-calendar 
              v-model="reservationData"
              :reservation-type="reservationType"
              @update:modelValue="updateReservationData"
            />
            <p v-if="errors.reservation" class="error-message">{{ errors.reservation }}</p>
          </div>

          <div class="form-group" v-if="reservationType === 'レンタル'">
            <label class="form-label">決済方法</label>
            <select class="form-control" v-model="paymentmethod">
              <option value="クレジット" selected>クレジット</option>
              <option value="銀行振込">銀行振込</option>
            </select>
            <p v-if="errors.paymentmethod" class="error-message">{{ errors.paymentmethod }}</p>
          </div>

          <div class="form-group">
            <label class="form-label">備考・ご質問等</label>
            <textarea 
              class="form-control" 
              v-model="comment" 
              rows="5"
              placeholder="ご質問やご要望がございましたらご記入ください"
            ></textarea>
          </div>

          <div class="form-group">
            <label class="form-label">利用規約</label>
            <div class="terms-wrapper">
              <textarea class="terms-content" readonly>●料金について
当サロンに会員登録していただきますと以下の料金でご利用いただけます。

★キャンペーン中につき全日特価となります。
【全日】
15分/360円（税込）
1時間/1,440円（税込）

※最低ご利用2時間からとなります。
※掃除時間も含めた時間でご予約下さい。
※ご予約時間からの入室となります。それより早いと前の方がご在室の場合がありますのでご注意下さい。


●当店は女性専用の会員制サロンです
まずはサロン見学をお願い致します。設備や備品についてご説明致します。その際ご利用目的・内容・経歴等をお聞きします。会員登録をご希望の場合は、申し込み書をお渡ししますのでご記入後メール・FAX・郵送にてご送付ください。
※平日10時から11時頃が見学ご案内をしやすいです。
※申し込み書が届いた後、審査をさせていただきます。1週間以内にメールまたはLINEにてご連絡させていただきます。

●キャンセルについて
はい、かかります。キャンセルするタイミングによってキャンセル料金は変わります。下記をご確認ください。
 • 8日より前:0%
 • 7日前〜4日前:25%
 • 3日前〜前日:50%
 • 当日:100%
※◯日前というのは23:59分を基準としております。例）1月1日予約の場合、12月31日23:59分までが前日キャンセル、それ以降は当日キャンセルとなります。それ意外のキャンセルに関しても同様となります。

●入退室時間について
入室は、前にご予約が入っていない場合は10分前から可能です。原則ご予約時間通りに入退室をお願いいたします（掃除も含めた時間をご予約下さい）前日に管理者よりご予約状況をお伝えします。
ご利用時間を超えてご利用になる場合は15分毎の延長料金を頂いております。
15分/360円
こちらを封筒をお使い頂き専用ボックスへ入れてください。
※入退室時間はセキュリティカメラにて記録されるようになっております。他のご利用者様の迷惑になるような場合はご利用を控えて頂く事がございます。

●賠償責任について
 • ご利用中の人的・物的損害等トラブルに関しては、全て申込者様に賠償請求をいたします。
 • 室内およびビル・マンションを破損・汚損等された場合、また、火災事故を起こした場合、全て申込者様に賠償請求をいたします。
 • ご利用後、特に清掃改修が必要な場合、またサロン内の設備や備品の破損においても、全て申込者様に別途費用を請求いたします。
 • また、上記により当サロンの営業に支障をきたした場合は、損害の実費負担と利用料金に基づいた逸失利益の合計金額を、全て申込者様に請求いたします。
 • 上記禁止行為に該当し、当サロン運営上に損害が生じたと判断した場合、賠償請求をいたします。

●免責事項について
 • ご利用中の出来事はすべて自己責任で行なってください。利用者様及びその関係者が当サロン内で起こした事故・損害・トラブル等に対して、弊社では一切責任を負いかねますので、あらかじめご了承ください。
 • 当サロンをご利用されたことに起因するあらゆる損害（直接的・間接的を問わず）に対して、弊社はいかなる責任も負いません。
 • 自然災害・暴動・政府規制等の不可抗力により利用ができない場合、また停電・設備の故障などのやむを得ない事情によりお部屋の貸出ができなくなった場合は、使用をお断りする場合がございます。その場合も弊社ではいかなる責任も負いません。
 • 貴重品やお荷物の紛失・盗難が起こっても、弊社はいかなる責任も負いません。必ず自己管理をお願いいたします。忘れ物をした場合はすぐに弊社にご連絡ください。弊社スタッフが見つけた場合は保管いたしますが、紛失・破損等があっても補償は致しません。(忘れ物の保管期限は14日間です)</textarea>
            </div>
            <label class="agreement-label">
              <input 
                type="checkbox" 
                v-model="privacyCheck"
                class="agreement-checkbox"
              >
              <span>利用規約に同意する</span>
            </label>
            <p v-if="errors.privacyCheck" class="error-message">{{ errors.privacyCheck }}</p>
          </div>

          <div class="form-actions">
            <base-button
              v-if="!valid"
              :disabled="true"
              variant="primary"
              size="lg"
              block
            >
              未入力の必須項目があります
            </base-button>
            <base-button
              v-else-if="valid && !isLoading"
              variant="primary"
              size="lg"
              block
              type="button"
              @click="submit"
            >
              予約を確定する
            </base-button>
            <base-button
              v-else-if="valid && isLoading"
              :disabled="true"
              variant="primary"
              size="lg"
              block
            >
              送信中...
            </base-button>
          </div>

          <transition name="fade">
            <div v-if="result" class="form-result">{{ result }}</div>
          </transition>
        </form>
      </div>
    </base-section>

    <!-- 確認モーダル -->
    <div v-if="showConfirmModal" class="modal-overlay" @click.self="cancelConfirm">
      <div class="modal-content">
        <h2 class="modal-title">
          <i class="fas fa-check-circle"></i>
          ご予約内容の確認
        </h2>
        
        <div class="confirm-section">
          <h3 class="confirm-section-title">
            <i class="fas fa-user"></i>
            お客様情報
          </h3>
          <dl class="confirm-list">
            <dt>お名前</dt>
            <dd>{{ name }} 様</dd>
            <dt>フリガナ</dt>
            <dd>{{ kana }}</dd>
            <dt>電話番号</dt>
            <dd>{{ tel }}</dd>
            <dt>メールアドレス</dt>
            <dd>{{ email }}</dd>
          </dl>
        </div>

        <div class="confirm-section">
          <h3 class="confirm-section-title">
            <i class="fas fa-calendar-check"></i>
            ご予約内容
          </h3>
          <dl class="confirm-list">
            <dt>ご利用目的</dt>
            <dd>{{ reservationType }}</dd>
            <dt>予約日</dt>
            <dd class="date-highlight">{{ formatDate(reservationData.date) }}</dd>
            <dt>開始時間</dt>
            <dd class="time-highlight">{{ reservationData.time }}</dd>
            <dt>利用時間</dt>
            <dd>{{ reservationData.duration }}分</dd>
            <dt>終了時間</dt>
            <dd class="time-highlight">{{ endTime }}</dd>
          </dl>
        </div>

        <div class="confirm-section payment-section" v-if="reservationType === 'レンタル' && paymentmethod === 'クレジット'">
          <h3 class="confirm-section-title">
            <i class="fas fa-credit-card"></i>
            お支払い情報
          </h3>
          <dl class="confirm-list">
            <dt>決済方法</dt>
            <dd>{{ needsNewCard ? 'クレジットカード（新規）' : 'クレジットカード（保存済み）' }}</dd>
            <dt>ご利用料金</dt>
            <dd class="price">¥{{ priceDisplay }}（税込）</dd>
          </dl>
          
          <!-- 新規カード入力フォーム -->
          <div v-if="needsNewCard" class="card-input-section">
            <p class="card-input-label">カード情報を入力してください</p>
            <div id="card-element" class="card-element"></div>
            <p v-if="cardErrors" class="card-error">{{ cardErrors }}</p>
            <label class="save-card-label">
              <input type="checkbox" v-model="saveCard" checked>
              <span>次回のために、このカードを保存する</span>
            </label>
          </div>
          
          <p class="payment-note">
            <i class="fas fa-info-circle"></i>
            決済を実行すると、{{ needsNewCard ? '入力された' : 'ご登録の' }}クレジットカードに即時請求されます。
          </p>
        </div>

        <div class="confirm-section" v-if="comment">
          <h3 class="confirm-section-title">
            <i class="fas fa-comment"></i>
            備考
          </h3>
          <p class="confirm-comment">{{ comment }}</p>
        </div>

        <!-- マイページ同時登録 -->
        <div class="confirm-section registration-section" v-if="!emailExists">
          <h3 class="confirm-section-title">
            <i class="fas fa-user-plus"></i>
            マイページ登録（無料）
          </h3>
          <div class="registration-box">
            <label class="registration-checkbox-label">
              <input type="checkbox" v-model="registerMypage">
              <span>マイページに同時登録する</span>
            </label>
            
            <p class="registration-note mt-2 mb-2">
              マイページに登録すると、キャンセルやカード情報の変更がマイページからできるようになります。
            </p>
            
            <p class="registration-note mb-2">
              その他、以下の機能が利用できます：
            </p>
            <ul class="registration-benefits">
              <li><i class="fas fa-check"></i> 次回から入力なしで簡単予約</li>
              <li><i class="fas fa-check"></i> 予約の確認・キャンセルがWebから可能</li>
              <li><i class="fas fa-check"></i> クレジットカード情報の保存・変更</li>
            </ul>
            
            <div v-if="registerMypage" class="registration-details">
              <div class="form-group mt-3">
                <label class="form-label text-start w-100">パスワード <span class="required">※必須</span></label>
                <input 
                  type="password" 
                  class="form-control" 
                  v-model="password" 
                  placeholder="8文字以上のパスワード"
                >
              </div>
              
              <div class="form-group mt-2">
                <label class="form-label text-start w-100">パスワード（確認用） <span class="required">※必須</span></label>
                <input 
                  type="password" 
                  class="form-control" 
                  v-model="passwordConfirm" 
                  placeholder="パスワードを再入力してください"
                >
              </div>
              
              <p v-if="registrationError" class="text-danger small mt-2">{{ registrationError }}</p>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <base-button
            variant="outline"
            size="lg"
            @click="cancelConfirm"
            :disabled="isProcessingPayment || isLoading"
          >
            <span v-if="!(isProcessingPayment || isLoading)">
              キャンセル
            </span>
            <span v-else>
              <i class="fas fa-times opacity-50"></i>
              キャンセル
            </span>
          </base-button>
          <base-button
            variant="primary"
            size="lg"
            @click="confirmAndProcess"
            :disabled="isProcessingPayment || isLoading"
            :class="{ 'processing': isProcessingPayment || isLoading }"
          >
            <span v-if="!(isProcessingPayment || isLoading)">
              {{ reservationType === 'レンタル' && paymentmethod === 'クレジット' ? '決済を実行' : '予約を確定' }}
            </span>
            <span v-else class="processing-content">
              <i class="fas fa-spinner fa-spin"></i>
              <span>{{ reservationType === 'レンタル' && paymentmethod === 'クレジット' ? '決済処理中...' : '予約処理中...' }}</span>
            </span>
          </base-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseSection from '@/components/common/BaseSection.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import ReserveCalendar from '@/components/ReserveCalendar.vue';
import { trackReservationComplete } from '@/utils/analytics';
import { useAuthStore } from '@/stores/auth';

export default {
  name: 'ReserveView',
  components: {
    BaseSection,
    BaseButton,
    ReserveCalendar
  },
  setup() {
    const authStore = useAuthStore();
    return { authStore };
  },
  data() {
    return {
      name: "",
      kana: "",
      tel: "",
      email: "",
      reservationType: "",
      customerStatus: null, // null, 'new', 'returning'
      customerId: null, // Stripe顧客ID
      hasPaymentMethod: false, // 保存済みカードの有無
      emailExists: false, // Firebase Authにアカウントが存在するか
      isCheckingCustomer: false,
      reservationData: {
        date: '',
        time: '',
        duration: 120
      },
      paymentmethod: "クレジット",
      comment: "",
      privacyCheck: false,
      errors: {
        name: "",
        kana: "",
        tel: "",
        email: "",
        reservationType: "",
        reservation: "",
        paymentmethod: "",
        privacyCheck: ""
      },
      result: "",
      isLoading: false,
      stripe: null, // Stripeインスタンス
      isProcessingPayment: false, // 決済処理中フラグ
      showConfirmModal: false, // 確認モーダル表示フラグ
      needsNewCard: false, // 新規カード入力が必要かどうか
      cardElement: null, // Stripe Card Element
      stripeElements: null, // Stripe Elements
      cardErrors: "", // カード入力エラー
      saveCard: true, // カードを保存するかどうか
      registerMypage: false, // マイページ同時登録
      password: "", // 登録用パスワード
      passwordConfirm: "", // パスワード確認用
      registrationError: "" // 登録エラー
    };
  },
  mounted() {
    // Stripe.jsの初期化
    if (window.Stripe) {
      // 本番環境用の公開可能キー（テスト環境の場合はテスト用キーを使用）
      const stripePublicKey = process.env.NODE_ENV === 'production'
        ? 'pk_live_51MY0vLFlmHlQhrEk0fr4o1DIAirl1XEnX1YO6gEvM9oC0Dck4Mjq9IXCEIPAS0xSTJqTtBhVfxFGOhOvPAUdGGn000xCCObNE8'
        : 'pk_live_51MY0vLFlmHlQhrEk0fr4o1DIAirl1XEnX1YO6gEvM9oC0Dck4Mjq9IXCEIPAS0xSTJqTtBhVfxFGOhOvPAUdGGn000xCCObNE8'; // 本番用キー
      this.stripe = window.Stripe(stripePublicKey);
      this.stripeElements = this.stripe.elements({
        locale: 'ja'
      });
    } else {
      console.error('Stripe.jsが読み込まれていません');
    }
  },
  computed: {
    valid() {
      let flg = true;
      if(!this.name || !this.tel || !this.email || !this.reservationType) {
        flg = false;
      }
      if(!this.reservationData.date || !this.reservationData.time) {
        flg = false;
      }
      if(!this.privacyCheck) {
        flg = false;
      }
      return flg;
    },
    endTime() {
      if (!this.reservationData.time || !this.reservationData.duration) return '';
      
      const [hours, minutes] = this.reservationData.time.replace('〜', '').split(':').map(Number);
      const endDate = new Date();
      endDate.setHours(hours, minutes, 0, 0);
      endDate.setMinutes(endDate.getMinutes() + this.reservationData.duration);
      
      return `${endDate.getHours()}:${String(endDate.getMinutes()).padStart(2, '0')}`;
    },
    priceDisplay() {
      const totalPrice = (this.reservationData.duration / 60) * 1440;
      return totalPrice.toLocaleString();
    }
  },
  methods: {
    updateReservationData(data) {
      this.reservationData = data;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekDay = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
      
      return `${year}年${month}月${day}日（${weekDay}）`;
    },
    async checkCustomerStatus() {
      if (!this.email || !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(this.email)) {
        this.customerStatus = null;
        this.reservationType = "";
        return;
      }

      this.isCheckingCustomer = true;
      this.customerStatus = null;
      this.reservationType = "";
      this.emailExists = false;

      try {
        // 1. Firebase Authにアカウントが存在するかチェック
        const emailCheckEndpoint = process.env.NODE_ENV === 'production'
          ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/checkEmailExists'
          : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/checkEmailExists';

        const emailCheckResponse = await this.axios.post(emailCheckEndpoint, {
          email: this.email
        });

        if (emailCheckResponse.data.success) {
          this.emailExists = emailCheckResponse.data.exists;
        }

        // 2. Stripe顧客情報をチェック
        const endpoint = process.env.NODE_ENV === 'production'
          ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/checkCustomerStatus'
          : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/checkCustomerStatus';

        const response = await this.axios.post(endpoint, {
          email: this.email
        });

        if (response.data.success) {
          if (response.data.isReturningCustomer) {
            this.customerStatus = 'returning';
            this.customerId = response.data.customerId; // Stripe顧客IDを保存
            this.hasPaymentMethod = response.data.hasPaymentMethod || false; // 保存済みカードの有無
            this.reservationType = 'レンタル';
            
            // 顧客情報をフォームに反映
            if (response.data.customerInfo) {
              if (response.data.customerInfo.name) {
                this.name = response.data.customerInfo.name;
              }
              if (response.data.customerInfo.phone) {
                this.tel = response.data.customerInfo.phone;
              }
            }
          } else {
            this.customerStatus = 'new';
            this.customerId = null;
            this.hasPaymentMethod = false;
            this.reservationType = '見学';
          }
        }
      } catch (error) {
        console.error('顧客ステータス確認エラー:', error);
        // CORSエラーまたはネットワークエラーの場合、APIを呼ばずにデフォルト処理
        // ユーザーは手動で予約タイプを選択できる
        this.customerStatus = null;
        this.reservationType = '';
      } finally {
        this.isCheckingCustomer = false;
      }
    },
    check() {
      this.errors = {
        name: "",
        kana: "",
        tel: "",
        email: "",
        reservationType: "",
        reservation: "",
        paymentmethod: "",
        privacyCheck: ""
      };
      let flg = true;
      // 必須項目のチェック
      if(!this.name) {
        this.errors.name = "お名前を入力してください";
        flg = false;
      }
      
      // フリガナは任意項目なので、入力された場合のみカタカナチェック
      if(this.kana && !/^[ァ-ヶー ]+$/.test(this.kana)) {
        this.errors.kana = "フリガナはカタカナで入力してください";
        flg = false;
      }
      
      if(!this.tel) {
        this.errors.tel = "電話番号を入力してください";
        flg = false;
      } else {
        // 国際電話形式（+81）または通常の日本の電話番号形式を許可
        const internationalPattern = /^\+81\d{9,11}$/;
        const domesticPattern = /^[0-9-]+$/;
        
        if (this.tel.startsWith('+81')) {
          if (!internationalPattern.test(this.tel)) {
            this.errors.tel = "国際電話番号は+81の後に9〜11桁の数字で入力してください";
            flg = false;
          }
        } else {
          if (!domesticPattern.test(this.tel) || this.tel.length < 10) {
            this.errors.tel = "電話番号は正しい形式で入力してください";
            flg = false;
          }
        }
      }
      
      if(!this.email) {
        this.errors.email = "メールアドレスを入力してください";
        flg = false;
      } else if(!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(this.email)) {
        this.errors.email = "メールアドレスは正しい形式で入力してください";
        flg = false;
      }
      
      if(!this.reservationType) {
        this.errors.reservationType = "ご利用目的を選択してください";
        flg = false;
      }
      
      if(!this.reservationData.date || !this.reservationData.time) {
        this.errors.reservation = "予約日時を選択してください";
        flg = false;
      }
      
      if(!this.privacyCheck) {
        this.errors.privacyCheck = "利用規約に同意してください";
        flg = false;
      }
      
      return flg;
    },
    async submit() {
      // 既に送信中の場合は何もしない
      if (this.isLoading || this.isProcessingPayment) {
        return;
      }
      
      if(this.check()) {
        // 確認モーダルを表示
        this.showConfirmModal = true;
        // カード入力が必要な場合は、モーダル表示後に判定
        if (this.reservationType === 'レンタル' && this.paymentmethod === 'クレジット') {
          if (!this.hasPaymentMethod) {
            // 保存済みカードがない場合は新規カード入力
            this.needsNewCard = true;
            this.$nextTick(() => {
              this.initializeCardElement();
            });
          } else {
            // 保存済みカードがある場合
            this.needsNewCard = false;
          }
        }
      }
    },
    
    async checkCardAvailability() {
      // 保存済みカードがあるか確認
      const paymentIntentEndpoint = process.env.NODE_ENV === 'production'
        ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/createPaymentIntent'
        : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/createPaymentIntent';
      
      try {
        const response = await this.axios.post(paymentIntentEndpoint, {
          amount: 100, // ダミー金額
          customerId: this.customerId,
          email: this.email,
          phone: this.tel,
          metadata: {}
        });
        
        if (!response.data.success && response.data.requiresNewPaymentMethod) {
          // 保存済みカードがない場合、Card Elementを初期化
          this.needsNewCard = true;
          this.$nextTick(() => {
            this.initializeCardElement();
          });
        }
      } catch (error) {
        console.error('カード確認エラー:', error);
      }
    },
    
    async handleRegistration() {
      if (!this.registerMypage || !this.password) return true;
      
      if (this.password.length < 8) {
        this.registrationError = "パスワードは8文字以上で入力してください";
        return false;
      }
      
      if (this.password !== this.passwordConfirm) {
        this.registrationError = "パスワードが一致しません";
        return false;
      }

      try {
        // 1. Firebase Authでユーザー登録
        await this.authStore.register(this.name, this.email, this.password);
        
        // 2. Stripe Customer IDが作成されるまでポーリング（最大10秒）
        let attempts = 0;
        const maxAttempts = 5;
        
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000)); // 2秒待機
          
          try {
            const endpoint = process.env.NODE_ENV === 'production'
              ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/checkCustomerStatus'
              : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/checkCustomerStatus';

            const response = await this.axios.post(endpoint, {
              email: this.email,
              uid: this.authStore.uid
            });

            if (response.data.success && response.data.customerId) {
              this.customerId = response.data.customerId;
              this.customerStatus = 'returning'; // 登録完了扱い
              return true;
            }
          } catch (e) {
            console.log('Polling check failed, retrying...', e);
          }
          attempts++;
        }
        
        // タイムアウトしても登録自体は成功しているので続行可能だが、
        // Stripe連携が間に合わない可能性があるため、警告を出すか、
        // そのまま進んで後で連携されることを期待する。
        // ここでは一旦trueを返して進める。
        console.warn('Stripe Customer ID creation timed out');
        return true;

      } catch (error) {
        console.error('Registration error:', error);
        if (error.code === 'auth/email-already-in-use') {
          this.registrationError = "このメールアドレスは既に登録されています";
        } else {
          this.registrationError = "登録処理に失敗しました: " + error.message;
        }
        return false;
      }
    },

    async confirmAndProcess() {
      // 二重実行を防ぐ
      if (this.isLoading || this.isProcessingPayment) {
        return;
      }
      
      this.isLoading = true;
      this.registrationError = "";
      
      try {
        // マイページ同時登録処理
        if (this.registerMypage) {
          const registrationSuccess = await this.handleRegistration();
          if (!registrationSuccess) {
            this.isLoading = false;
            return;
          }
        }

        // レンタルでクレジット決済の場合はStripe決済を実行
        if (this.reservationType === 'レンタル' && this.paymentmethod === 'クレジット') {
          await this.processStripePayment();
        } else {
          // 見学または銀行振込の場合は従来の処理
          await this.createReservation();
        }
      } catch (error) {
        console.error('予約処理エラー:', error);
        this.isLoading = false;
      }
    },
    
    async createReservation() {
      // 見学の場合はpaymentmethodを設定しない
      let paymentMethodValue = '';
      if (this.reservationType === 'レンタル') {
        paymentMethodValue = this.paymentmethod || '銀行振込';
      } else if (this.reservationType === '見学') {
        paymentMethodValue = '見学（決済なし）';
      }
      
      const params = {
        name: this.name,
        kana: this.kana,
        tel: this.tel,
        email: this.email,
        date: this.reservationData.date,
        time: this.reservationData.time,
        duration: this.reservationData.duration,
        endTime: this.reservationData.endTime || null,
        reservationType: this.reservationType,
        paymentmethod: paymentMethodValue,
        comment: this.comment,
        totalPrice: this.reservationType === 'レンタル' ? (this.reservationData.duration / 60) * 1440 : 0
      };
      
      // Firebase Functions エンドポイント（カレンダー登録機能付き）
      const endpoint = process.env.NODE_ENV === 'production' 
        ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/createReservationWithCalendar'
        : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/createReservationWithCalendar';
      
      try {
        const response = await this.axios.post(endpoint, params);
        
        if (response.data.success) {
          this.result = "予約が確定しました";
          this.showConfirmModal = false;
          
          // GA4コンバージョン追跡
          trackReservationComplete({
            reservation_date: this.reservationData.date,
            payment_method: this.paymentmethod || '見学'
          });
          
          // 自動返信メールの案内を表示
          alert('ご予約が確定しました。\n自動返信メールをお送りしましたのでご確認ください。');
          
          this.$router.push('/thanks');
        }
      } catch (error) {
        console.error('予約作成エラー:', error);
        this.result = "予約の作成に失敗しました";
        alert('予約の作成に失敗しました。\n恐れ入りますが、時間をおいて再度お試しください。');
      } finally {
        this.isLoading = false;
      }
    },
    
    cancelConfirm() {
      this.showConfirmModal = false;
      this.needsNewCard = false;
      this.cardErrors = "";
      // Card Elementを破棄
      if (this.cardElement) {
        this.cardElement.destroy();
        this.cardElement = null;
      }
    },
    
    initializeCardElement() {
      if (!this.stripeElements || !document.getElementById('card-element')) return;
      
      // 既存のCard Elementがある場合は破棄
      if (this.cardElement) {
        this.cardElement.destroy();
        this.cardElement = null;
      }
      
      // Card Elementのスタイル設定
      const style = {
        base: {
          color: '#32325d',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      };
      
      // Card Elementを作成
      this.cardElement = this.stripeElements.create('card', { 
        style: style,
        hidePostalCode: true
      });
      
      // Card Elementをマウント
      this.cardElement.mount('#card-element');
      
      // エラーハンドリング
      this.cardElement.on('change', (event) => {
        this.cardErrors = event.error ? event.error.message : '';
      });
    },
    
    async processStripePayment() {
      if (!this.stripe || !this.customerId) {
        alert('決済処理の初期化に失敗しました。お問い合わせフォームよりご連絡ください。');
        this.isLoading = false;
        return;
      }
      
      this.isProcessingPayment = true;
      
      try {
        // 料金計算
        const amount = (this.reservationData.duration / 60) * 1440;
        
        let paymentIntentId;
        
        if (this.needsNewCard) {
          // 新規カードの場合
          if (!this.cardElement) {
            alert('カード情報の入力フォームが初期化されていません。');
            this.isLoading = false;
            this.isProcessingPayment = false;
            return;
          }
          
          // Payment Methodを作成
          const { error: pmError, paymentMethod } = await this.stripe.createPaymentMethod({
            type: 'card',
            card: this.cardElement,
            billing_details: {
              name: this.name,
              email: this.email
            }
          });
          
          if (pmError) {
            console.error('Payment Method作成エラー:', pmError);
            alert('カード情報の処理に失敗しました: ' + pmError.message);
            this.isLoading = false;
            this.isProcessingPayment = false;
            return;
          }
          
          // 新規カードでPayment Intentを作成
          const paymentIntentEndpoint = process.env.NODE_ENV === 'production'
            ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/createPaymentIntentWithNewCard'
            : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/createPaymentIntentWithNewCard';
          
          console.log('新規カード決済エンドポイント:', paymentIntentEndpoint);
          console.log('PaymentMethod ID:', paymentMethod.id);
          
          const response = await this.axios.post(paymentIntentEndpoint, {
            amount: amount,
            customerId: this.customerId,
            paymentMethodId: paymentMethod.id,
            saveCard: this.saveCard,
            email: this.email,
            phone: this.tel,
            metadata: {
              name: this.name,
              date: this.reservationData.date,
              time: this.reservationData.time,
              duration: this.reservationData.duration,
              endTime: this.reservationData.endTime || null
            }
          });
          
          // PaymentIntentIdを先に取得（requiresActionの場合も含む）
          paymentIntentId = response.data.paymentIntentId;
          
          if (response.data.requiresAction) {
            // 3Dセキュア認証が必要な場合
            console.log('3Dセキュア認証が必要です。');
            console.log('paymentIntentId:', paymentIntentId);
            console.log('clientSecret:', response.data.clientSecret);
            
            const { error, paymentIntent } = await this.stripe.confirmCardPayment(response.data.clientSecret);
            
            if (error) {
              console.error('3Dセキュア認証エラー:', error);
              alert('決済の認証に失敗しました: ' + error.message);
              this.isLoading = false;
              this.isProcessingPayment = false;
              return;
            }
            
            // 認証成功後、PaymentIntent IDを更新（通常は同じID）
            console.log('3Dセキュア認証成功。PaymentIntent:', paymentIntent);
            if (paymentIntent && paymentIntent.id) {
              paymentIntentId = paymentIntent.id;
            }
          }
          
        } else {
          // 保存済みカードの場合
          const paymentIntentEndpoint = process.env.NODE_ENV === 'production'
            ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/createPaymentIntent'
            : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/createPaymentIntent';
          
          const response = await this.axios.post(paymentIntentEndpoint, {
            amount: amount,
            customerId: this.customerId,
            email: this.email,
            phone: this.tel,
            metadata: {
              name: this.name,
              date: this.reservationData.date,
              time: this.reservationData.time,
              duration: this.reservationData.duration,
              endTime: this.reservationData.endTime || null
            }
          });
          
          // PaymentIntentIdを先に取得（requiresActionの場合も含む）
          paymentIntentId = response.data.paymentIntentId;
          
          if (response.data.requiresAction) {
            // 3Dセキュア認証が必要な場合
            console.log('3Dセキュア認証が必要です（保存済みカード）。');
            console.log('paymentIntentId:', paymentIntentId);
            console.log('clientSecret:', response.data.clientSecret);
            
            const { error, paymentIntent } = await this.stripe.confirmCardPayment(response.data.clientSecret);
            
            if (error) {
              console.error('3Dセキュア認証エラー:', error);
              alert('決済の認証に失敗しました: ' + error.message);
              this.isLoading = false;
              this.isProcessingPayment = false;
              return;
            }
            
            // 認証成功後、PaymentIntent IDを更新（通常は同じID）
            console.log('3Dセキュア認証成功。PaymentIntent:', paymentIntent);
            if (paymentIntent && paymentIntent.id) {
              paymentIntentId = paymentIntent.id;
            }
          }
        }
        
        // 決済確認と予約作成
        const confirmEndpoint = process.env.NODE_ENV === 'production'
          ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/confirmPaymentAndCreateReservation'
          : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/confirmPaymentAndCreateReservation';
        
        const confirmResponse = await this.axios.post(confirmEndpoint, {
          paymentIntentId: paymentIntentId,
          reservationData: {
            name: this.name,
            kana: this.kana,
            tel: this.tel,
            email: this.email,
            date: this.reservationData.date,
            time: this.reservationData.time,
            duration: this.reservationData.duration,
            endTime: this.reservationData.endTime || null,
            comment: this.comment,
            totalPrice: amount
          }
        });
        
        if (confirmResponse.data.success) {
          this.result = "予約と決済が完了しました";
          this.showConfirmModal = false;
          
          // GA4コンバージョン追跡
          trackReservationComplete({
            reservation_date: this.reservationData.date,
            payment_method: 'クレジット（即時決済）',
            payment_amount: amount
          });
          
          // 自動返信メールの案内を表示
          alert('ご予約と決済が完了しました。\n自動返信メールをお送りしましたのでご確認ください。');
          
          this.$router.push('/thanks');
        } else {
          throw new Error(confirmResponse.data.error || '予約の作成に失敗しました');
        }
      } catch (error) {
        console.error('決済処理エラー:', error);
        
        // エラーメッセージの判定
        if (error.response?.data?.requiresContact) {
          alert('システムエラーが発生しました。\nお手数ですが、お問い合わせフォームよりご連絡ください。');
        } else {
          alert('決済処理に失敗しました。\n' + (error.message || '時間をおいて再度お試しください。'));
        }
      } finally {
        this.isProcessingPayment = false;
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.reserve-view {
  padding-top: 0;
}

// ページヘッダー
.page-hero {
  background: linear-gradient(135deg, $primary-color 0%, $primary-light 100%);
  color: $white;
  padding: $spacing-4xl 0;
  text-align: center;
  
  @include media-breakpoint-down(md) {
    padding: $spacing-3xl 0;
  }
  
  &__title {
    font-size: $font-size-4xl;
    font-weight: 700;
    margin-bottom: $spacing-md;
    color: $white;
    
    @include media-breakpoint-down(md) {
      font-size: $font-size-3xl;
    }
  }
  
  &__subtitle {
    font-size: $font-size-xl;
    opacity: 0.9;
    
    @include media-breakpoint-down(md) {
      font-size: $font-size-lg;
    }
  }
}

// フォームラッパー
.reserve-form-wrapper {
  max-width: 720px;
  margin: 0 auto;
}

// フォーム
.reserve-form {
  background: $white;
  border-radius: $radius-lg;
  padding: $spacing-2xl;
  box-shadow: $shadow-sm;
  
  @include media-breakpoint-down(md) {
    padding: $spacing-xl;
  }
  
  @include media-breakpoint-down(sm) {
    padding: 0;
  }
}

// フォームグループ
.form-group {
  margin-bottom: $spacing-xl;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.form-label {
  display: block;
  font-weight: 600;
  color: $gray-800;
  margin-bottom: $spacing-sm;
  font-size: $font-size-md;
  
  .required {
    color: $danger;
    font-size: $font-size-sm;
    margin-left: $spacing-xs;
  }
}

.form-control {
  width: 100%;
  padding: $spacing-sm $spacing-md;
  font-size: $font-size-md;
  line-height: 1.5;
  color: $gray-800;
  background-color: $white;
  border: 1px solid $gray-300;
  border-radius: $radius-md;
  transition: all $transition-base;
  
  &:focus {
    outline: none;
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
  }
  
  &.has-error {
    border-color: $danger;
  }
  
  &::placeholder {
    color: $gray-500;
  }
}

select.form-control {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right $spacing-md center;
  padding-right: $spacing-2xl;
}

textarea.form-control {
  resize: vertical;
  min-height: 120px;
}

// エラーメッセージ
.error-message {
  color: $danger;
  font-size: $font-size-sm;
  margin-top: $spacing-xs;
  margin-bottom: 0;
}

// 日時入力グループ
.date-time-group {
  margin-bottom: $spacing-md;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.date-label {
  display: block;
  font-weight: 500;
  color: $gray-700;
  margin-bottom: $spacing-xs;
  font-size: $font-size-sm;
}

.date-time-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $spacing-sm;
  
  @include media-breakpoint-down(sm) {
    grid-template-columns: 1fr;
  }
}

// ラジオボタン・チェックボックス
.radio-group,
.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-md;
}

.radio-label,
.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: $font-size-md;
  color: $gray-700;
  
  input {
    margin-right: $spacing-xs;
  }
  
  &:hover {
    color: $primary-color;
  }
}

// フォームアクション
.form-actions {
  margin-top: $spacing-2xl;
  text-align: center;
}

// 結果メッセージ
.form-result {
  margin-top: $spacing-lg;
  padding: $spacing-md;
  background-color: $success;
  color: $white;
  border-radius: $radius-md;
  text-align: center;
  font-weight: 600;
}

// カレンダー通知
.calendar-notice {
  background-color: rgba($primary-color, 0.1);
  color: $primary-color;
  padding: $spacing-sm $spacing-md;
  border-radius: $radius-md;
  margin-bottom: $spacing-md;
  font-size: $font-size-sm;
  
  i {
    margin-right: $spacing-xs;
  }
}

// カレンダーラッパー
.calendar-wrapper {
  border: 1px solid $gray-300;
  border-radius: $radius-md;
  overflow: hidden;
  margin-bottom: $spacing-md;
}

// 利用規約
.terms-wrapper {
  margin-bottom: $spacing-md;
}

.terms-content {
  width: 100%;
  height: 300px;
  padding: $spacing-md;
  font-size: $font-size-sm;
  line-height: 1.6;
  color: $gray-700;
  background-color: $gray-100;
  border: 1px solid $gray-300;
  border-radius: $radius-md;
  resize: none;
  overflow-y: auto;
}

// 同意チェックボックス
.agreement-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: $font-size-md;
  color: $gray-700;
  
  .agreement-checkbox {
    margin-right: $spacing-sm;
  }
  
  &:hover {
    color: $primary-color;
  }
}

// トランジション
.fade-enter-active,
.fade-leave-active {
  transition: opacity $transition-base;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// メールアドレス入力欄のラッパー
.form-input-wrapper {
  position: relative;
}

// メールアドレス欄のローディング表示
.email-loading {
  position: absolute;
  right: $spacing-md;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  color: $gray-600;
  font-size: $font-size-sm;
  
  i {
    margin-right: $spacing-xs;
    font-size: $font-size-md;
  }
  
  span {
    @include media-breakpoint-down(sm) {
      display: none; // モバイルではアイコンのみ表示
    }
  }
}

// 予約タイプ選択
.customer-status-loading {
  display: flex;
  align-items: center;
  color: $gray-600;
  padding: $spacing-md 0;
  
  i {
    margin-right: $spacing-sm;
  }
}

.reservation-type-options {
  display: flex;
  gap: $spacing-md;
  margin-bottom: $spacing-md;
}

.reservation-type-option {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: $spacing-md $spacing-lg;
  background: $white;
  border: 2px solid $gray-300;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all $transition-base;
  
  &:hover:not(.disabled) {
    border-color: $primary-color;
    background: rgba($primary-color, 0.05);
  }
  
  input[type="radio"] {
    margin-right: $spacing-sm;
  }
  
  input[type="radio"]:checked + span {
    color: $primary-color;
    font-weight: 600;
  }
  
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      border-color: $gray-300;
      background: $white;
    }
  }
}

.info-message {
  display: flex;
  align-items: center;
  margin: 0;
  padding: $spacing-sm $spacing-md;
  background: rgba($info, 0.1);
  color: $info;
  border-radius: $radius-md;
  font-size: $font-size-sm;
  
  i {
    margin-right: $spacing-sm;
  }
}

// モーダル
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: $spacing-md;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: $white;
  border-radius: $radius-lg;
  padding: $spacing-2xl;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  
  @include media-breakpoint-down(md) {
    padding: $spacing-xl;
  }
  
  @include media-breakpoint-down(sm) {
    padding: $spacing-lg;
    max-width: 95%;
  }
}

.modal-title {
  font-size: $font-size-2xl;
  font-weight: 700;
  color: $primary-color;
  margin-bottom: $spacing-2xl;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-sm;
  
  i {
    color: $accent-color;
    font-size: $font-size-3xl;
  }
  
  @include media-breakpoint-down(sm) {
    font-size: $font-size-xl;
    
    i {
      font-size: $font-size-2xl;
    }
  }
}

.confirm-section {
  margin-bottom: $spacing-xl;
  background: $gray-50;
  padding: $spacing-lg;
  border-radius: $radius-md;
  
  &:last-of-type {
    margin-bottom: $spacing-2xl;
  }
  
  &.payment-section {
    background: lighten($accent-color, 45%);
    border: 1px solid lighten($accent-color, 30%);
  }
}

.confirm-section-title {
  font-size: $font-size-lg;
  font-weight: 600;
  color: $primary-color;
  margin-bottom: $spacing-lg;
  padding-bottom: $spacing-sm;
  border-bottom: 2px solid $primary-light;
  display: flex;
  align-items: center;
  gap: $spacing-sm;
  
  i {
    color: $accent-color;
    font-size: $font-size-md;
  }
}

.confirm-list {
  dt {
    font-weight: 600;
    color: $gray-600;
    margin-bottom: $spacing-xs;
    font-size: $font-size-sm;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  dd {
    margin-bottom: $spacing-lg;
    color: $gray-800;
    font-size: $font-size-md;
    padding-left: $spacing-md;
    
    &.price {
      font-size: $font-size-2xl;
      font-weight: 700;
      color: $primary-color;
      background: $white;
      padding: $spacing-sm $spacing-md;
      border-radius: $radius-sm;
      display: inline-block;
      border: 2px solid $accent-color;
    }
    
    &.date-highlight {
      font-weight: 600;
      color: $primary-dark;
      font-size: $font-size-lg;
    }
    
    &.time-highlight {
      font-weight: 600;
      color: $accent-dark;
    }
  }
}

.confirm-comment {
  color: $gray-700;
  line-height: 1.6;
}

.payment-note {
  display: flex;
  align-items: flex-start;
  margin-top: $spacing-lg;
  padding: $spacing-md $spacing-lg;
  background: linear-gradient(135deg, rgba($accent-color, 0.05), rgba($warning, 0.05));
  color: $primary-dark;
  border-radius: $radius-md;
  font-size: $font-size-sm;
  border-left: 4px solid $accent-color;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  i {
    margin-right: $spacing-sm;
    margin-top: 2px;
    flex-shrink: 0;
    color: $accent-color;
    font-size: $font-size-md;
  }
}

.modal-actions {
  display: flex;
  gap: $spacing-lg;
  justify-content: space-between;
  margin-top: $spacing-2xl;
  padding-top: $spacing-xl;
  border-top: 2px solid $gray-200;
  
  button {
    flex: 1;
    max-width: 220px;
    font-weight: 600;
    padding: $spacing-md $spacing-xl;
    
    &:first-child {
      margin-right: auto;
    }
    
    &:last-child {
      margin-left: auto;
    }
  }
  
  @include media-breakpoint-down(sm) {
    flex-direction: column-reverse;
    gap: $spacing-sm;
    
    button {
      width: 100%;
      max-width: none;
      margin: 0;
    }
  }
}

// ボタン処理中スタイル
.processing {
  position: relative;
  opacity: 0.85;
  cursor: wait !important;
  
  &:hover {
    transform: none !important;
  }
}

.processing-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-sm;
  
  i {
    font-size: $font-size-lg;
    animation: spin 1s linear infinite;
  }
  
  span {
    font-weight: 600;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.opacity-50 {
  opacity: 0.5;
}

// Stripe Card Element
.card-input-section {
  margin-top: $spacing-lg;
  padding: $spacing-lg;
  background: $gray-100;
  border-radius: $radius-md;
}

.card-input-label {
  font-weight: 600;
  color: $gray-700;
  margin-bottom: $spacing-md;
}

.card-element {
  padding: $spacing-md;
  background: $white;
  border: 1px solid $gray-300;
  border-radius: $radius-md;
  min-height: 44px;
  
  &.StripeElement--focus {
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
  }
  
  &.StripeElement--invalid {
    border-color: $danger;
  }
}

.card-error {
  color: $danger;
  font-size: $font-size-sm;
  margin-top: $spacing-sm;
}

.save-card-label {
  display: flex;
  align-items: center;
  margin-top: $spacing-md;
  cursor: pointer;
  font-size: $font-size-sm;
  color: $gray-700;
  
  input[type="checkbox"] {
    margin-right: $spacing-sm;
  }
}

.profile-role {
  font-size: 0.8rem;
}

.registration-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
}

.registration-box {
  background-color: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
}

.registration-checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
}

.registration-checkbox-label input[type="checkbox"] {
  width: 1.2rem;
  height: 1.2rem;
}

.registration-details {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
  animation: fadeIn 0.3s ease;
}

.registration-note {
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: #495057;
}

.registration-benefits {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
}

.registration-benefits li {
  font-size: 0.85rem;
  color: #6a4229;
  margin-bottom: 0.25rem;
}

.registration-benefits li i {
  margin-right: 0.5rem;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>